import i18n from 'i18n';
import { ChannelType } from 'core/channelGroup/ChannelGroup';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { isImageAudioFamily, OutdoorType } from 'core/creative/Creative';

/**
 * 這個銷售渠道是不是走電子紙看板。
 *
 * ECOPD-5581：判定依據是渠道自己的 channelType —— 系統管理員在後台把渠道建成電子紙
 * 就生效，不需要為了新增一個電子紙 site 去改設定檔重佈署。
 *
 * 用 findById 而不是 fromId：這支被 9 個地方呼叫，全都在渲染路徑上。fromId 查不到
 * 會 throw，而動態渠道在 registry init 完成前本來就查不到 —— 那會把整個畫面炸掉。
 * findById 查不到回 undefined，也就是「不認得就當作不是電子紙」。
 *
 * ⚠️ 這條判定綁著 registry：registry 由 ChannelGroupSetting.init() 灌入，走
 * getChannelGroups()。ECOPD-5566 起那支**不再過濾**停用（ARCHIVED）渠道 ——
 * 停用渠道照樣註冊，過濾改在各「可選清單」出口用 activeValues() 做。所以停用的
 * 電子紙渠道在這支眼裡仍然是電子紙，底下的素材／活動頁面也照樣開得起來。
 *
 * 這是刻意的：ECOPD-5566 的目的就是讓停用渠道底下的既有訂單 / 群組 / 活動 / 素材
 * 仍看得到。若哪天需要「停用就不算電子紙」，要另外併 isEffectivelyArchivedChannel()
 * 判定，不要指望 registry 幫你濾掉。
 *
 * 收斂成一支函式是因為前端已經有兩處要看這件事（活動的固定時段、素材的綁定與啟用），
 * 各自比對一次會讓兩邊有機會走偏。
 */
export function isEpaperChannelGroup(channelGroupId?: number): boolean {
  if (channelGroupId === undefined) {
    return false;
  }
  return (
    ChannelGroupSetting.findById(channelGroupId)?.config.channelType ===
    ChannelType.E_PAPER
  );
}

/**
 * 這檔活動鎖到的電子紙機台，**今天**已經有排不進去的 —— 綁定或啟用素材都要擋。
 *
 * ECOPD-5640。語意刻意是「當下」而不是「走期內」，與 `storeOverBindingLimitCount`
 * 這個欄位本身的定義一致（`CampaignListDTO` 的註解：「這檔活動鎖到的門市／機台『當下』
 * （今天）已經被 10 個活動選滿，它排不進去。刻意不看走期」）。
 *
 * ### 為什麼是「當下」而不是「走期」
 *
 * `EpaperCapacitySpaceFilter` 的 class 說明把電子紙名額分成三條刻意不同粒度的鏈：
 * 選機台時灰掉哪些（走期）· 活動能不能啟用（走期）· **現在這個門市投不投得出去（當下）**。
 * 「啟用一份素材」改變的正是最後那件事，所以問「當下」。
 * 「整個走期承諾得起嗎」那一層的把關留在它該在的地方——活動的啟用鍵與編輯投放限制時的
 * 版位灰掉，兩者都用走期版。
 *
 * ### 🔴 為什麼**不**用 `epaperActivatable`
 *
 * 那個欄位的契約明文只回答「（停用中的）活動能不能被啟用」——
 * `AecoSignDeviceCapacityService#needsActivatableCheck` 的 javadoc：「只有停用中的活動需要
 * 這個答案。已啟用（ACTIVATE）的活動不問，欄位留 null。」而停用中的活動**根本不佔名額**
 * （`AecoSignCampaignClaimService#desiredClaims` 要求活動 ACTIVATE 才建排隊憑證）⇒
 * 拿它來擋素材，擋到的全是不會出事的情形，會出事的那一半（在跑的活動上啟用素材）
 * 因為欄位是 null 而完全擋不到。
 *
 * ### ⚠️ 邊界（fail-open，與走期版一致，不是本欄位特有）
 *
 * 缺值時當成 0（後端 `CampaignListDTO` 的預設也是 0），所以「後端沒算」與「沒有滿的」
 * 分不開；沒有設定任何投放限制的活動不會進後端的版位篩選迴圈，欄位停在 0 也不擋。
 * 走期版在同樣情況同樣放行（`applyActivatable` 對沒有版位解析結果的活動刻意不給答案）。
 * 渠道判定保留在這裡：非電子紙渠道這個欄位恆為 0，多一道判定是為了讓意圖看得出來。
 */
export function isEpaperSpaceQuotaFull(campaign: {
  channelGroupId?: number;
  storeOverBindingLimitCount?: number;
}): boolean {
  return (
    isEpaperChannelGroup(campaign.channelGroupId) &&
    (campaign.storeOverBindingLimitCount ?? 0) > 0
  );
}

/**
 * 電子紙時長的顯示文字。
 *
 * 值一直是秒（送後端的單位不變），只有給人看的地方拆成分與秒 —— 電子紙的時長動輒好幾
 * 分鐘，用純秒數講要讀的人自己換算一次。滾 0 的那一段省略（「6 分鐘」、「45 秒」）。
 */
export function formatEpaperDuration(durationSeconds: number): string {
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  const minutesText = `${minutes} ${i18n.t('common.units.minutes')}`;
  const secondsText = `${seconds} ${i18n.t('common.units.seconds')}`;
  if (minutes === 0) {
    return secondsText;
  }
  return seconds === 0 ? minutesText : `${minutesText} ${secondsText}`;
}

/**
 * 這份素材的播放時長要不要用 `formatEpaperDuration` 顯示。
 *
 * 要同時是電子紙渠道、而且走「圖+音」那條分支（IMAGE_AUDIO 或純圖片 IMAGE）：秒數自訂
 * 只套用在這條分支，只看渠道會讓別的類型的秒數也被拆成分（影片的 30 秒會變成
 * 「0 分鐘 30 秒」）。
 *
 * 收斂成一支函式的理由同 isEpaperChannelGroup：素材摘要頁與素材列表要用同一套講法，
 * 各自比對一次就會出現「摘要寫 6 分鐘、列表寫 360 秒」的落差。
 */
export function usesEpaperDurationFormat(
  channelGroupId: number | undefined,
  outdoorType: OutdoorType | undefined
): boolean {
  return (
    isEpaperChannelGroup(channelGroupId) && isImageAudioFamily(outdoorType)
  );
}

/**
 * 戶外廣告類型的顯示文字。
 *
 * 電子紙看板沒有喇叭（supportAudio 對電子紙一律回 false，音檔欄位整個藏起來），
 * IMAGE_AUDIO 這個類型在電子紙的情境實際上只有圖片，沿用共用的「圖 + 音」會讓 BD
 * 誤以為還要準備音檔（POS-8070）。類型還沒正規化前，先在顯示層按渠道換文字——送後端
 * 的 outdoorType 值與行為都不變，只有給人看的字改掉。
 *
 * 收斂成一支函式的理由同 isEpaperChannelGroup：素材表單、摘要頁與素材列表都要用同一套
 * 講法，各自 i18n.t 一次就會出現「新增時寫圖片、列表寫圖 + 音」的落差。
 */
export function getOutdoorTypeLabel(
  outdoorType: OutdoorType,
  channelGroupId?: number
): string {
  if (
    outdoorType === OutdoorType.IMAGE_AUDIO &&
    isEpaperChannelGroup(channelGroupId)
  ) {
    return i18n.t('outdoorForm.labels.outdoorType2Epaper');
  }
  return i18n.t(`outdoorForm.labels.outdoorType${outdoorType}`);
}
